import useOctadeskChatStore from '../stores/useOctadeskChatStore'
import {
  OCTADESK_APP_ID,
  OCTADESK_CHAT_MAX_HEIGHT_PX,
  OCTADESK_CHAT_MAX_WIDTH_PX,
  OCTADESK_CHAT_RIGHT,
  OCTADESK_CHAT_Z_INDEX,
  OCTADESK_DOM_SELECTOR,
  OCTADESK_FAB_GAP_PX,
  OCTADESK_LAYOUT_BURST_MAX,
  OCTADESK_LAYOUT_RETRY_DELAYS_MS,
  OCTADESK_PANEL_LAYOUT_MIN_HEIGHT_PX,
  OCTADESK_PANEL_LAYOUT_MIN_WIDTH_PX
} from '../constants/octadeskConstants'

let panelVisibilityObserver: MutationObserver | null = null
let assistantSidebarLayoutObserver: MutationObserver | null = null
let octadeskAppLayoutObserver: MutationObserver | null = null
let observedOctadeskAppElement: HTMLElement | null = null
let syncFrameId: number | null = null
let layoutBurstFrameId: number | null = null
let layoutBurstCount = 0
let isApplyingPointerEvents = false
let isApplyingOpenLayout = false
let layoutRetryTimeouts: ReturnType<typeof setTimeout>[] = []
let layoutDebounceTimeoutId: ReturnType<typeof setTimeout> | null = null
let lastLayoutSignature = ''

export function resetOctadeskLayoutSignature (): void {
  lastLayoutSignature = ''
}

function isOctadeskNode (node: Node): boolean {
  if (!(node instanceof HTMLElement)) {
    return false
  }

  return node.matches(OCTADESK_DOM_SELECTOR) ||
    Boolean(node.closest(OCTADESK_DOM_SELECTOR))
}

export function getOctadeskAppElement (): HTMLElement | null {
  return document.getElementById(OCTADESK_APP_ID) ??
    document.querySelector<HTMLElement>('.octadesk-octachat-app.octachatAppOpen') ??
    document.querySelector<HTMLElement>('.octadesk-octachat-app:not(.octadesk-octachat-app-hide)')
}

export function isOctadeskChatPanelVisible (): boolean {
  const app = getOctadeskAppElement()
  if (!app) {
    return false
  }

  if (app.classList.contains('octadesk-octachat-app-hide')) {
    return false
  }

  if (app.classList.contains('octachatAppClose')) {
    return false
  }

  return app.classList.contains('octachatAppOpen')
}

export function applyOctadeskPointerEvents (): void {
  if (isApplyingPointerEvents) {
    return
  }

  isApplyingPointerEvents = true

  try {
    const store = useOctadeskChatStore()
    const open = store.isOpen
    const app = getOctadeskAppElement()

    document.querySelectorAll<HTMLElement>(OCTADESK_DOM_SELECTOR).forEach((element) => {
      element.style.setProperty('pointer-events', 'none', 'important')

      if (!open) {
        element.style.setProperty('width', '0', 'important')
        element.style.setProperty('height', '0', 'important')
        element.style.setProperty('overflow', 'hidden', 'important')
        element.style.setProperty('visibility', 'hidden', 'important')
      }
    })

    if (open && app) {
      app.style.setProperty('pointer-events', 'auto', 'important')
      app.style.setProperty('visibility', 'visible', 'important')

      app.querySelectorAll<HTMLElement>('iframe').forEach((iframe) => {
        iframe.style.setProperty('pointer-events', 'auto', 'important')
        iframe.style.setProperty('visibility', 'visible', 'important')
      })
    }
  } finally {
    isApplyingPointerEvents = false
  }
}

function getOctadeskLayoutBox (): { bottomPx: number, heightPx: number } {
  const viewportHeight = window.innerHeight
  const topMarginPx = viewportHeight <= 680 ? 56 : 72
  const fabElement = document.querySelector('.floating-chat-actions')

  let bottomPx: number
  let maxHeight: number

  if (fabElement instanceof HTMLElement) {
    const fabRect = fabElement.getBoundingClientRect()
    bottomPx = Math.round(viewportHeight - fabRect.bottom + OCTADESK_FAB_GAP_PX)
    maxHeight = fabRect.top - topMarginPx
  } else {
    bottomPx = 88
    maxHeight = viewportHeight - bottomPx - 56 - topMarginPx
  }

  const heightPx = Math.max(
    OCTADESK_PANEL_LAYOUT_MIN_HEIGHT_PX,
    Math.min(OCTADESK_CHAT_MAX_HEIGHT_PX, maxHeight)
  )

  return { bottomPx, heightPx }
}

function getAssistantSidebarWidthPx (): number {
  if (!document.documentElement.classList.contains('assistant-sidebar-open')) {
    return 0
  }

  const assistantRaw = getComputedStyle(document.documentElement)
    .getPropertyValue('--assistant-sidebar-width')
    .trim()

  return Number.parseFloat(assistantRaw) || 0
}

function getOctadeskChatWidthPx (): number {
  const viewportWidth = window.innerWidth
  const leftSidebarRaw = getComputedStyle(document.documentElement)
    .getPropertyValue('--layout-left-sidebar-width')
    .trim()

  const leftSidebarPx = Number.parseFloat(leftSidebarRaw) || 256
  const assistantPx = getAssistantSidebarWidthPx()

  return Math.max(
    OCTADESK_PANEL_LAYOUT_MIN_WIDTH_PX,
    Math.min(
      OCTADESK_CHAT_MAX_WIDTH_PX,
      viewportWidth - leftSidebarPx - assistantPx - 48
    )
  )
}

function getOctadeskChatRightOffset (): string {
  const assistantPx = getAssistantSidebarWidthPx()

  if (assistantPx > 0) {
    return `calc(${assistantPx}px + ${OCTADESK_CHAT_RIGHT})`
  }

  return OCTADESK_CHAT_RIGHT
}

function resetOctadeskIframeLayout (iframe: HTMLIFrameElement): void {
  iframe.style.setProperty('position', 'absolute', 'important')
  iframe.style.setProperty('inset', '0', 'important')
  iframe.style.setProperty('width', '100%', 'important')
  iframe.style.setProperty('height', '100%', 'important')
  iframe.style.setProperty('max-width', 'none', 'important')
  iframe.style.setProperty('max-height', 'none', 'important')
  iframe.style.setProperty('min-height', '0', 'important')
  iframe.style.setProperty('margin', '0', 'important')
  iframe.style.setProperty('transform', 'none', 'important')
}

function applyOctadeskOpenLayout (): void {
  const store = useOctadeskChatStore()

  if (!store.isOpen || isApplyingOpenLayout) {
    return
  }

  const app = getOctadeskAppElement()
  if (!app || !isOctadeskChatPanelVisible()) {
    return
  }

  const right = getOctadeskChatRightOffset()
  const { bottomPx, heightPx } = getOctadeskLayoutBox()
  const widthPx = getOctadeskChatWidthPx()
  const signature = `${bottomPx}:${heightPx}:${widthPx}:${right}`

  if (signature === lastLayoutSignature) {
    return
  }

  lastLayoutSignature = signature
  isApplyingOpenLayout = true

  try {
    document.documentElement.style.setProperty('--octadesk-chat-bottom', `${bottomPx}px`)
    document.documentElement.style.setProperty('--octadesk-chat-height', `${heightPx}px`)
    document.documentElement.style.setProperty('--octadesk-chat-width', `${widthPx}px`)

    app.style.setProperty('position', 'fixed', 'important')
    app.style.setProperty('inset', 'auto', 'important')
    app.style.setProperty('top', 'auto', 'important')
    app.style.setProperty('bottom', `${bottomPx}px`, 'important')
    app.style.setProperty('left', 'auto', 'important')
    app.style.setProperty('right', right, 'important')
    app.style.setProperty('width', `${widthPx}px`, 'important')
    app.style.setProperty('height', `${heightPx}px`, 'important')
    app.style.setProperty('max-width', `${widthPx}px`, 'important')
    app.style.setProperty('max-height', `${heightPx}px`, 'important')
    app.style.setProperty('min-width', '0', 'important')
    app.style.setProperty('min-height', '0', 'important')
    app.style.setProperty('margin', '0', 'important')
    app.style.setProperty('margin-top', '0', 'important')
    app.style.setProperty('padding', '0', 'important')
    app.style.setProperty('transform', 'none', 'important')
    app.style.setProperty('overflow', 'hidden', 'important')
    app.style.setProperty('z-index', `${OCTADESK_CHAT_Z_INDEX}`, 'important')
    app.style.setProperty('display', 'block', 'important')

    app.querySelectorAll<HTMLIFrameElement>('iframe').forEach(resetOctadeskIframeLayout)
    startWatchingOctadeskAppLayout(app)
  } finally {
    isApplyingOpenLayout = false
  }
}

function stopWatchingOctadeskAppLayout (): void {
  octadeskAppLayoutObserver?.disconnect()
  octadeskAppLayoutObserver = null
  observedOctadeskAppElement = null
}

function startWatchingOctadeskAppLayout (app: HTMLElement): void {
  if (observedOctadeskAppElement === app && octadeskAppLayoutObserver !== null) {
    return
  }

  stopWatchingOctadeskAppLayout()
  observedOctadeskAppElement = app

  octadeskAppLayoutObserver = new MutationObserver(() => {
    if (!useOctadeskChatStore().isOpen || isApplyingOpenLayout) {
      return
    }

    const expectedRight = getOctadeskChatRightOffset()
    const currentRight = app.style.getPropertyValue('right')

    if (currentRight && currentRight !== expectedRight) {
      lastLayoutSignature = ''
      applyOctadeskOpenLayout()
    }
  })

  octadeskAppLayoutObserver.observe(app, {
    attributes: true,
    attributeFilter: ['style', 'class']
  })
}

function clearLayoutDebounce (): void {
  if (layoutDebounceTimeoutId !== null) {
    clearTimeout(layoutDebounceTimeoutId)
    layoutDebounceTimeoutId = null
  }
}

function scheduleDebouncedLayout (): void {
  clearLayoutDebounce()

  layoutDebounceTimeoutId = setTimeout(() => {
    layoutDebounceTimeoutId = null

    if (!useOctadeskChatStore().isOpen) {
      return
    }

    applyOctadeskOpenLayout()
    applyOctadeskPointerEvents()
  }, 80)
}

function clearLayoutRetries (): void {
  layoutRetryTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
  layoutRetryTimeouts = []
}

function scheduleLayoutRetries (): void {
  clearLayoutRetries()

  OCTADESK_LAYOUT_RETRY_DELAYS_MS.forEach((delayMs) => {
    const timeoutId = setTimeout(() => {
      if (!useOctadeskChatStore().isOpen) {
        return
      }

      lastLayoutSignature = ''
      applyOctadeskOpenLayout()
      applyOctadeskPointerEvents()
    }, delayMs)

    layoutRetryTimeouts.push(timeoutId)
  })
}

export function ensureOctadeskPanelLayout (): void {
  lastLayoutSignature = ''
  applyOctadeskOpenLayout()
  applyOctadeskPointerEvents()
  scheduleLayoutBurst()
  scheduleLayoutRetries()
}

function stopLayoutBurst (): void {
  layoutBurstCount = 0

  if (layoutBurstFrameId !== null) {
    cancelAnimationFrame(layoutBurstFrameId)
    layoutBurstFrameId = null
  }
}

function scheduleLayoutBurst (): void {
  stopLayoutBurst()

  const tick = (): void => {
    layoutBurstFrameId = null

    if (!useOctadeskChatStore().isOpen || layoutBurstCount >= OCTADESK_LAYOUT_BURST_MAX) {
      layoutBurstCount = 0
      return
    }

    layoutBurstCount += 1
    lastLayoutSignature = ''
    applyOctadeskOpenLayout()
    applyOctadeskPointerEvents()
    layoutBurstFrameId = requestAnimationFrame(tick)
  }

  layoutBurstFrameId = requestAnimationFrame(tick)
}

function cancelScheduledSync (): void {
  if (syncFrameId !== null) {
    cancelAnimationFrame(syncFrameId)
    syncFrameId = null
  }
}

function scheduleSyncFromDom (): void {
  if (syncFrameId !== null) {
    return
  }

  syncFrameId = requestAnimationFrame(() => {
    syncFrameId = null
    syncOctadeskFromDom()
  })
}

function syncOctadeskFromDom (): void {
  const store = useOctadeskChatStore()
  const panelVisible = isOctadeskChatPanelVisible()

  if (store.openRequested) {
    if (!panelVisible) {
      return
    }

    if (!store.isOpen) {
      store.setOpen(true)
    }

    ensureOctadeskPanelLayout()
    return
  }

  if (panelVisible) {
    store.markOpenRequested()
    store.setOpen(true)
    ensureOctadeskPanelLayout()
    return
  }

  if (store.isOpen) {
    store.setOpen(false)
    resetOctadeskLayoutSignature()
  }

  applyOctadeskPointerEvents()
}

function detectOctadeskClosedByUser (): void {
  const store = useOctadeskChatStore()

  if (!store.openRequested || isOctadeskChatPanelVisible()) {
    return
  }

  store.clearOpenRequested()
  store.setOpen(false)
  resetOctadeskLayoutSignature()
  stopLayoutBurst()
  clearLayoutRetries()
  clearLayoutDebounce()
  applyOctadeskPointerEvents()
}

export function stopWatchingOctadeskPanelVisibility (): void {
  panelVisibilityObserver?.disconnect()
  panelVisibilityObserver = null
  cancelScheduledSync()
  stopLayoutBurst()
  clearLayoutRetries()
  clearLayoutDebounce()
}

function isMeaningfulOctadeskMutation (mutation: MutationRecord): boolean {
  if (mutation.type === 'childList') {
    return Array.from(mutation.addedNodes).some(isOctadeskNode) ||
      Array.from(mutation.removedNodes).some(isOctadeskNode)
  }

  if (mutation.type === 'attributes') {
    if (mutation.attributeName === 'hidden') {
      return isOctadeskNode(mutation.target)
    }

    if (mutation.attributeName === 'class' && isOctadeskNode(mutation.target)) {
      const target = mutation.target
      if (!(target instanceof HTMLElement)) {
        return false
      }

      return target.classList.contains('octachatAppOpen') ||
        target.classList.contains('octachatAppClose') ||
        target.classList.contains('octadesk-octachat-app-hide')
    }
  }

  return false
}

export function startWatchingOctadeskPanelVisibility (): void {
  if (panelVisibilityObserver !== null) {
    scheduleSyncFromDom()
    return
  }

  panelVisibilityObserver = new MutationObserver((mutations) => {
    const hasMeaningfulChange = mutations.some(isMeaningfulOctadeskMutation)
    if (!hasMeaningfulChange) {
      return
    }

    applyOctadeskPointerEvents()

    const store = useOctadeskChatStore()

    if (store.openRequested) {
      if (isOctadeskChatPanelVisible()) {
        scheduleDebouncedLayout()
      } else {
        detectOctadeskClosedByUser()
      }

      return
    }

    scheduleSyncFromDom()
  })

  panelVisibilityObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'hidden']
  })

  scheduleSyncFromDom()
}

export function handleOctadeskAssistantShellLayoutChange (): void {
  if (!useOctadeskChatStore().isOpen) {
    return
  }

  ensureOctadeskPanelLayout()
}

export function handleOctadeskViewportChange (): void {
  if (!useOctadeskChatStore().isOpen) {
    return
  }

  lastLayoutSignature = ''
  scheduleDebouncedLayout()
}

function handleAssistantSidebarLayoutChange (): void {
  handleOctadeskAssistantShellLayoutChange()
}

export function startWatchingAssistantSidebarLayout (): void {
  if (assistantSidebarLayoutObserver !== null) {
    return
  }

  assistantSidebarLayoutObserver = new MutationObserver((mutations) => {
    const hasAssistantLayoutChange = mutations.some(mutation =>
      mutation.type === 'attributes' &&
      mutation.target === document.documentElement &&
      (mutation.attributeName === 'class' || mutation.attributeName === 'style')
    )

    if (hasAssistantLayoutChange) {
      handleAssistantSidebarLayoutChange()
    }
  })

  assistantSidebarLayoutObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style']
  })
}

export function stopWatchingAssistantSidebarLayout (): void {
  assistantSidebarLayoutObserver?.disconnect()
  assistantSidebarLayoutObserver = null
}

export function stopOctadeskLayoutEffects (): void {
  stopLayoutBurst()
  clearLayoutRetries()
  clearLayoutDebounce()
  stopWatchingOctadeskAppLayout()
  stopWatchingAssistantSidebarLayout()
}
